import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';
import { AddressStats } from './AddressProfile/AddressStats';
import { AddressProfile } from './AddressProfile/AddressProfile';
import { CopyButton } from 'src/components/ui/copy-button';
import { AddressSummarySkeleton } from './AddressSummary.skeleton';
import { useAddressAndChainParams } from 'src/hooks/routing/useAddressAndChainParams';
import { useAddressParam } from 'src/hooks/routing/useAddressParam';
import { BeneficialOwner } from './AddressProfile/BeneficialOwner';
import { AddressQRCode } from './AddressProfile/AddressQRCode';
import { useAddressData, useAddressStats } from 'src/hooks/useAddressData';

export const AddressSummary = () => {
  const { address, chain } = useAddressAndChainParams();

  // Use for loading state only - React Query will dedupe with AddressProfile's call
  const { isLoading: isLoadingData, error: dataError } = useAddressData(
    address,
    chain,
  );

  const {
    data: addressStats,
    isLoading: isLoadingStats,
    error: statsError,
  } = useAddressStats(address, chain);

  if (!address) return null;

  // Show loading only if the main data (balance) is loading
  if (isLoadingData) {
    return <AddressSummarySkeleton />;
  }

  if (dataError) {
    return (
      <div className="mb-6 w-full border-b border-muted-200 bg-background dark:border-muted-800">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6">
          <div className="text-destructive">
            Error loading address data:{' '}
            {dataError instanceof Error ? dataError.message : 'Unknown error'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="mx-auto mb-6 w-full max-w-[1500px] rounded-lg border border-muted-50 bg-background shadow-[0_0_10px_rgba(0,0,0,0.1)] dark:border-muted-200">
        <div className="px-4 py-6 sm:px-6">
          <AddressProfile />

          <div className="flex flex-col gap-4 lg:flex-row">
            <div className="lg:max-w-[50%] lg:flex-shrink-0">
              <div className="mb-0.5 text-sm capitalize text-muted-foreground">
                {chain} Address
              </div>
              <AddressDisplay />
              <BeneficialOwner />
            </div>

            <div className="min-w-0 flex-1">
              <AddressStats
                stats={addressStats}
                isLoading={isLoadingStats}
                error={statsError}
              />
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

const AddressCopy = () => {
  const address = useAddressParam();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <CopyButton text={address} iconSize={16} />
      </TooltipTrigger>
      <TooltipContent>
        <p>Copy address</p>
      </TooltipContent>
    </Tooltip>
  );
};

const AddressDisplay = () => {
  const address = useAddressParam();

  return (
    <div className="flex items-center">
      <div className="overflow-x-auto break-all pr-2 text-sm text-foreground lg:whitespace-nowrap lg:break-normal">
        {address}
      </div>
      <AddressCopy />
      <AddressQRCode />
    </div>
  );
};
